import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { AlertTriangle, CheckCircle2, Circle, Lock, Minus, Plus, Wand2 } from 'lucide-react';
import { HelpBubble } from '@/help/HelpBubble';
import { rowsOf } from '@/data/items';
import { AREA_LABEL, AREA_SECTIONS, countId, editBlockReason, fillUsualAmounts, recountStillPossible, SECTION_LABEL, setAreaDone, setCount } from '@/data/nights';
import { rt } from '@/data/runtime';
import { addDays, formatBusinessDate, isBusinessDate } from '@/domain/businessDate';
import { emptyWithSpout, spoutOf } from '@/domain/bottles';
import { entryWarning } from '@/domain/entryCheck';
import { SECTION_KIND, type AreaId, type CountEntry, type Item, type Night, type SectionId } from '@/domain/types';
import { Button, cn, EmptyState, Notice, ProgressBar, Spinner } from '@/ui/core';
import { NumberInput } from '@/ui/form';
import { useConfirm, useToast } from '@/ui/overlay';
import { ButtonLink } from '@/ui/ButtonLink';
import { useSession } from '@/app/providers';
import { useRecountDay, useWakeLock } from '@/app/hooks';
import { RecountDone } from './RecountList';
import { VoiceBar } from './VoiceBar';
import { areaProgress, useNightData } from './useNight';

const COUNT_AREAS: AreaId[] = ['back_beer', 'front_beer', 'liquor'];
const HELP: Record<string, string> = { back_beer: 'count-back-beer', front_beer: 'count-front-beer', liquor: 'count-liquor' };

/**
 * Counting one area, like filling in its column on the sheet: every drink in the sheet's
 * order, one number each. Beer is whole bottles/cans with big − and + buttons; liquor is
 * ounces, typed as written on the sheet. Numbers save by themselves.
 */
export function AreaCount() {
  const { date = '', area = '' } = useParams();
  const [params] = useSearchParams();
  const focusItem = params.get('item');
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { actor, people } = useSession();
  const data = useNightData(date);
  // Each drink's last count on each list (within a week), for the "is that right?" check.
  const prev = useLiveQuery(async () => {
    if (!isBusinessDate(date)) return new Map<string, number>();
    const rows = await rt().db.counts.where('date').between(addDays(date, -7), addDays(date, -1), true, true).toArray();
    const latest = new Map<string, { date: string; value: number }>();
    for (const r of rows) {
      if (r.value == null) continue;
      const k = `${r.section}|${r.itemId}`;
      const cur = latest.get(k);
      if (!cur || r.date > cur.date) latest.set(k, { date: r.date, value: r.value });
    }
    return new Map([...latest].map(([k, v]) => [k, v.value]));
  }, [date]);
  const [busy, setBusy] = useState(false);
  const oldestRecount = useRecountDay();
  useWakeLock();

  useEffect(() => {
    if (!focusItem || !data) return;
    const el = document.getElementById(`row-${focusItem}`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el?.querySelector('input')?.focus({ preventScroll: true });
    // Only once, when the page opens on a recount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusItem, !!data]);

  if (!isBusinessDate(date) || !COUNT_AREAS.includes(area as AreaId)) return <EmptyState title="That page doesn’t exist" action={<ButtonLink to="/">Home</ButtonLink>} />;
  if (!data) return <Spinner />;
  const a = area as AreaId;
  const progress = areaProgress(data, date, a);
  // Recounts asked for this night that can still be done (none after the recount time the next day).
  const openRecounts = recountStillPossible(date, oldestRecount) ? progress.recounts : 0;
  const night = data.night;
  const done = night?.areasDone[a];
  const doneBy = done ? people.find((p) => p.id === done.by)?.name : null;
  const sections = AREA_SECTIONS[a];
  const usualLeft = a === 'front_beer' ? rowsOf(data.items, 'front_beer').filter((it) => it.usualFront != null && data.counts.get(countId(date, 'front_beer', it.id))?.value == null).length : 0;
  const locked = editBlockReason(night, a, undefined, actor.role);
  const missing = progress.total - progress.counted;

  const finish = async () => {
    setBusy(true);
    try {
      // Let any number still being typed save first.
      (document.activeElement as HTMLElement | null)?.blur();
      await new Promise((r) => setTimeout(r, 50));
      await setAreaDone(date, a, true);
      toast.show(`${AREA_LABEL[a]} is done. Thank you!`);
      navigate('/');
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-base text-muted md:text-lg">{formatBusinessDate(date, 'en-CA', 'long')}</p>
          <h1 className="flex items-center gap-1 text-3xl font-bold md:text-4xl">
            {AREA_LABEL[a]} <HelpBubble topic={HELP[a]!} />
          </h1>
        </div>
        <div className="w-full max-w-xs">
          <p className="mb-1 text-right text-base font-semibold">
            {progress.counted} of {progress.total} counted
          </p>
          <ProgressBar value={progress.total ? (progress.counted / progress.total) * 100 : 0} label={`${AREA_LABEL[a]} progress`} tone={missing === 0 ? 'ok' : 'accent'} />
        </div>
      </div>

      {done && (
        <Notice
          tone="ok"
          icon={<CheckCircle2 className="size-5" aria-hidden />}
          title={`Done${doneBy ? ` by ${doneBy}` : ''}`}
          action={
            night?.status === 'counting' || (night?.status === 'finished' && actor.role !== 'staff') ? (
              <Button
                onClick={async () => {
                  try {
                    await setAreaDone(date, a, false);
                  } catch (err) {
                    toast.error(err);
                  }
                }}
              >
                Change something
              </Button>
            ) : undefined
          }
        >
          {openRecounts > 0 ? 'A manager asked for the drinks marked in yellow to be counted again. You can change those.' : 'These numbers are saved.'}
        </Notice>
      )}
      {locked && !done && (
        <Notice tone="info" icon={<Lock className="size-5" aria-hidden />}>
          {locked}
          {openRecounts > 0 && ' The drinks marked in yellow can still be changed.'}
        </Notice>
      )}

      {a === 'front_beer' && usualLeft > 0 && !locked && !done && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border-2 border-info/50 bg-info-soft p-3 md:p-4">
          <Wand2 className="size-7 shrink-0 text-info" aria-hidden />
          <p className="min-w-48 flex-1 text-base">
            <strong>Filled up as usual?</strong> Put in the usual amount for the {usualLeft} drink{usualLeft === 1 ? '' : 's'} not counted yet, then fix any that are different.
          </p>
          <span className="flex items-center">
            <Button
              size="lg"
              variant="primary"
              onClick={async () => {
                const ok = await confirm({ title: 'Fill in the usual amounts?', body: `This puts the usual amount in for ${usualLeft} drink${usualLeft === 1 ? '' : 's'}. Look at each one after and change any that are different.`, confirmLabel: 'Fill them in' });
                if (!ok) return;
                try {
                  const n = await fillUsualAmounts(date);
                  toast.show(`Filled in ${n}. Now check each one.`);
                } catch (err) {
                  toast.error(err);
                }
              }}
            >
              Fill in usual amounts
            </Button>
            <HelpBubble topic="usual-amounts" />
          </span>
        </div>
      )}

      {!done && !locked && progress.total > 0 && <VoiceBar date={date} area={a} rows={sections.flatMap((s) => rowsOf(data.items, s).map((item) => ({ item, section: s, value: data.counts.get(countId(date, s, item.id))?.value ?? null })))} />}

      {sections.map((s) => {
        const list = rowsOf(data.items, s);
        return (
          <section key={s} aria-labelledby={`h-${s}`} className="flex flex-col gap-2">
            {sections.length > 1 && (
              <h2 id={`h-${s}`} className="mt-2 text-2xl font-bold">
                {SECTION_LABEL[s]}
              </h2>
            )}
            {list.length === 0 ? (
              <EmptyState title="Nothing on this list">A manager adds drinks in the manager area (Drink lists).</EmptyState>
            ) : (
              <ul className={cn('grid gap-2', SECTION_KIND[s] === 'beer' ? 'lg:grid-cols-2' : 'md:grid-cols-2')}>
                {list.map((it) => (
                  <li key={it.id} id={`row-${it.id}`} className="scroll-mt-28">
                    <CountRow
                      date={date}
                      section={s}
                      area={a}
                      item={it}
                      entry={data.counts.get(countId(date, s, it.id))}
                      night={night}
                      prev={prev?.get(`${s}|${it.id}`) ?? null}
                      added={data.moves.filter((m) => m.itemId === it.id && m.kind === 'added').reduce((t, m) => t + m.qty, 0)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {!done && !locked && (
        <div className="sticky bottom-3 z-10 mt-2 flex flex-col gap-2 rounded-3xl border-2 border-border bg-surface/95 p-3 shadow-lg backdrop-blur md:flex-row md:items-center">
          <p className="flex-1 text-base md:text-lg">
            {missing === 0 ? (
              <strong>Everything has a number.</strong>
            ) : (
              <>
                <strong>{missing}</strong> still {missing === 1 ? 'has' : 'have'} no number. If there are none, type 0.
              </>
            )}
          </p>
          <Button size="xl" variant={missing === 0 ? 'ok' : 'secondary'} busy={busy} disabled={missing > 0} onClick={finish} icon={<CheckCircle2 className="size-6" aria-hidden />}>
            {AREA_LABEL[a]} is done
          </Button>
        </div>
      )}
      {(done || locked) && (
        <ButtonLink to="/" size="xl" variant="primary" block>
          Back to the home screen
        </ButtonLink>
      )}
    </div>
  );
}

type Parts = { boxes: number | null; singles: number | null };

/**
 * Back beer with a box size: full boxes plus singles. Only the total is saved, so the sheet,
 * the checks and the Excel file see one number as before.
 */
function BoxCount({ value, size, onChange, onBlur, disabled, label }: { value: number | null; size: number; onChange: (v: number | null) => void; onBlur: () => void; disabled: boolean; label: string }) {
  const split = (v: number | null): Parts => (v == null ? { boxes: null, singles: null } : { boxes: Math.floor(v / size), singles: v % size });
  const total = (p: Parts) => (p.boxes == null && p.singles == null ? null : (p.boxes ?? 0) * size + (p.singles ?? 0));
  const [parts, setParts] = useState<Parts>(() => split(value));
  // Changed from somewhere else (voice, the other device, a recount): show it as boxes and singles again.
  useEffect(() => {
    if (value !== total(parts)) setParts(split(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, size]);
  const set = (next: Parts) => {
    setParts(next);
    onChange(total(next));
  };
  const stepper = (key: keyof Parts, caption: string) => {
    const v = parts[key];
    return (
      <div className="flex flex-col items-center gap-1">
        <div className="flex items-stretch gap-1.5">
          <button type="button" disabled={disabled || v == null || v <= 0} onClick={() => set({ ...parts, [key]: Math.max(0, (v ?? 0) - 1) })} aria-label={`One ${key === 'boxes' ? 'box' : 'single'} less, ${label}`} className="flex size-12 shrink-0 items-center justify-center rounded-xl border border-border bg-surface-2 disabled:opacity-40 md:size-14">
            <Minus className="size-6" aria-hidden />
          </button>
          <NumberInput aria-label={`${label}: ${caption}`} value={v} onValueChange={(n) => set({ ...parts, [key]: n })} disabled={disabled} size="xl" align="center" placeholder="—" className="w-16 md:w-20" />
          <button type="button" disabled={disabled} onClick={() => set({ ...parts, [key]: (v ?? 0) + 1 })} aria-label={`One ${key === 'boxes' ? 'box' : 'single'} more, ${label}`} className="flex size-12 shrink-0 items-center justify-center rounded-xl border border-border bg-surface-2 disabled:opacity-40 md:size-14">
            <Plus className="size-6" aria-hidden />
          </button>
        </div>
        <span className="text-sm font-semibold text-muted">{caption}</span>
      </div>
    );
  };
  return (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-2" onBlur={onBlur}>
      {stepper('boxes', `boxes of ${size}`)}
      {stepper('singles', 'singles')}
      <span className="self-center pb-6 text-lg font-bold tabular">= {value ?? '—'}</span>
    </div>
  );
}

/**
 * One drink: its name and one number. Saves half a second after the last change (and when
 * leaving the box), so fast tapping doesn't write every step.
 */
function CountRow({ date, section, area, item, entry, night, prev, added }: { date: string; section: SectionId; area: AreaId; item: Item; entry: CountEntry | undefined; night: Night | undefined; prev: number | null; added: number }) {
  const { actor } = useSession();
  const toast = useToast();
  const kind = SECTION_KIND[section];
  const stored = entry?.value ?? null;
  const [value, setValue] = useState<number | null>(stored);
  const dirty = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef(value);
  latest.current = value;
  const lastSaved = useRef(stored);
  useEffect(() => {
    if (dirty.current) return;
    setValue(stored);
    lastSaved.current = stored;
  }, [stored]);

  const flush = async () => {
    window.clearTimeout(timer.current);
    const v = latest.current;
    if (v === lastSaved.current) {
      dirty.current = false;
      return;
    }
    try {
      await setCount(date, section, item.id, v);
      lastSaved.current = v;
      dirty.current = false;
    } catch (err) {
      toast.error(err);
      dirty.current = false;
      setValue(lastSaved.current);
    }
  };
  const change = (v: number | null) => {
    setValue(v);
    dirty.current = true;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flush(), 500);
  };
  useEffect(
    () => () => {
      if (dirty.current) void flush();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const oldestRecount = useRecountDay();
  const blocked = editBlockReason(night, area, entry, actor.role, oldestRecount);
  const recount = !!entry?.recountRequestedAt && recountStillPossible(date, oldestRecount);
  const counted = value != null;
  // Checked on the saved number (not while typing), and quiet once someone says it's right.
  const [okFor, setOkFor] = useState<number | null>(null);
  const warning = blocked || stored === okFor ? null : entryWarning({ kind, section, value: stored, prev, added, usual: item.usualFront });
  const label = `${item.name}${section === 'front_beer' ? ' (front)' : ''}`;
  return (
    <div className={cn('flex flex-col gap-2 rounded-2xl border-2 bg-surface p-2 pl-3', recount || warning ? 'border-warn' : counted ? 'border-ok/50' : 'border-border')}>
      {recount && <RecountDone compact date={date} itemId={item.id} area={area} flush={flush} note={entry?.recountNote} />}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="flex min-h-14 min-w-44 flex-1 items-center gap-2">
          {counted ? <CheckCircle2 className="size-6 shrink-0 text-ok" aria-label="Counted" /> : <Circle className="size-6 shrink-0 text-muted/60" aria-label="Not counted yet" />}
          <span className="min-w-0">
            <span className="block text-lg font-semibold leading-tight md:text-xl">{item.name}</span>
            {section === 'front_beer' && item.usualFront != null && <span className="block text-sm text-muted">Usually {item.usualFront}</span>}
            {kind === 'liquor' && item.colB != null && item.colB !== '' && (
              <span className="block text-sm text-muted">{emptyWithSpout(item) != null ? (spoutOf(item) ? `Empty with spout: ${emptyWithSpout(item)} oz` : `Empty bottle: ${emptyWithSpout(item)} oz`) : `Empty bottle: ${String(item.colB)}`}</span>
            )}
          </span>
        </span>
        {section === 'back_beer' && item.caseSize ? (
          <BoxCount value={value} size={item.caseSize} onChange={change} onBlur={() => void flush()} disabled={!!blocked} label={label} />
        ) : kind === 'beer' ? (
          <div className="flex items-stretch gap-2">
            <button type="button" disabled={!!blocked || value == null || value <= 0} onClick={() => change(Math.max(0, (value ?? 0) - 1))} aria-label={`One less ${label}`} className="flex size-14 shrink-0 items-center justify-center rounded-xl border border-border bg-surface-2 disabled:opacity-40 md:size-16">
              <Minus className="size-7" aria-hidden />
            </button>
            <NumberInput aria-label={`${label}: bottles or cans`} value={value} onValueChange={change} onBlur={() => void flush()} disabled={!!blocked} size="xl" align="center" placeholder="—" className="w-24 md:w-28" />
            <button type="button" disabled={!!blocked} onClick={() => change((value ?? 0) + 1)} aria-label={`One more ${label}`} className="flex size-14 shrink-0 items-center justify-center rounded-xl border border-border bg-surface-2 disabled:opacity-40 md:size-16">
              <Plus className="size-7" aria-hidden />
            </button>
          </div>
        ) : (
          <label className="flex items-center gap-2">
            <NumberInput aria-label={`${label}: ounces`} value={value} onValueChange={change} onBlur={() => void flush()} decimals disabled={!!blocked} size="xl" align="center" placeholder="—" className="w-32 md:w-36" />
            <span className="text-lg font-semibold text-muted">oz</span>
          </label>
        )}
      </div>
      {warning && (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-xl bg-warn-soft p-2 text-base">
          <AlertTriangle className="size-5 shrink-0 text-warn" aria-hidden />
          <span className="min-w-48 flex-1">{warning}</span>
          <Button size="sm" onClick={() => setOkFor(stored)}>
            Yes, it’s right
          </Button>
        </div>
      )}
    </div>
  );
}
